import ks from "../ReUsableTS/kBrowserShared";
import { khtmlButton } from "../ReUsableTS/kHTML";
import { kStyleClass, kStyles } from "./kStyles";
import { kFormTheme } from "./kFormTheme";

/**
 * kModalDialogStyles - the page-level stylesheets and font warm-up every kModalDialog shares: the
 * top-right x class, the shared focus / reduced-motion sheet, and Preinstall. Static, no dialog state.
 * Moved out of kModalDialog unchanged (2026-10-20 split); kModalDialog.Preinstall delegates here.
 */
export class kModalDialogStyles {

    /** The dialog's own font stack: ONE string, because the constructor writes it on every
     *  container and Preinstall has to warm exactly the faces that stack resolves to. */
    public static readonly FontFamily: string = "Segoe UI, Segoe, Tahoma, Helvetica, Arial, sans-serif";
    /** The hidden text Preinstall puts in the page to get the dialog's fonts loaded; null when
     *  none is in the page. */
    private static _FontProbe: HTMLDivElement | null = null;
    /** True once the font probe has been placed, so a second Preinstall adds nothing. */
    private static _FontsWarmed: boolean = false;

    /** One document-level stylesheet, installed once per page (kStyles.AddStyles is
     *  id-guarded, so repeat calls are free). It carries the two things inline styles
     *  cannot express: a VISIBLE keyboard focus ring on the top-right × (:focus-visible,
     *  so a mouse click leaves no ring), and a prefers-reduced-motion block that
     *  neutralises every transition/animation on the dialog — including any a host
     *  stylesheet puts on .kDialogContainer / .kDialogContent. */
    /** The top-right × class (kStyles), configured once. */
    public static CloseStyle = (): kStyleClass => {
        if (ks.KErrorMode === true) { return new kStyleClass(); }
        try {
            const kCloseStyle: kStyleClass = kStyles.AddStyle("kDialogTopRightClose");
            if (kCloseStyle.Saved === true) { return kCloseStyle; }
            const CurrStyle: CSSStyleDeclaration = kCloseStyle.Style;
            CurrStyle.position = "absolute";
            CurrStyle.top = "8px";
            CurrStyle.right = "12px";
            CurrStyle.cursor = "pointer";
            CurrStyle.display = "flex";
            CurrStyle.alignItems = "center";
            CurrStyle.justifyContent = "center";
            CurrStyle.width = "24px";
            CurrStyle.height = "24px";
            CurrStyle.borderRadius = "4px";
            // DARK, not white. This span is a child of .kDialogContent (#fefefe) and is
            // absolutely positioned in the top-right corner, so on any dialog whose title
            // bar is NOT a saturated colour block - a plain message dialog, or one using
            // SetAccentTitle's near-white bar - white text on a near-white background is
            // an invisible close button. Measured 2026-08-04: contrast ratio 1.01 against
            // #fefefe, where 4.5 is the minimum. SetGreyTitle switches it back to white,
            // because there it really does sit on the coloured bar.
            CurrStyle.color = "#424a53";
            CurrStyle.fontSize = "18px";
            CurrStyle.lineHeight = "1";
            return kCloseStyle;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kModalDialogStyles.CloseStyle");
            return new kStyleClass();
        }
    }

    /**
     * Install every stylesheet a dialog needs NOW, without opening one (all id-guarded: later calls and
     * later dialogs add nothing). Call it at app start, while the page is still small.
     *
     * Why (measured 2026-09-29, DocumentBuilder, 7,300-element editor): the FIRST dialog of a page added
     * three stylesheets - kStyleSheet, kDialogSharedStyles and kFormThemeStyle, whose `:root` custom
     * properties make the browser restyle EVERY element - inside the click, which cost ~100 ms of style
     * recalc plus a full layout before the dialog could paint. The second dialog cost 15 ms. Any app whose
     * first dialog opens over a large page pays the same; a second consumer only has to call this once.
     *
     * Two more things the first dialog still did inside the click (measured 2026-10-01, the same editor,
     * an EMPTY dialog: 16 ms of main-thread CPU and 40-240 ms on the clock for the first, 2.5 ms for the
     * second - DocumentBuilder PX-125), now done here as well:
     *   - the OK / Cancel button's `.kHTMLButton` rule was inserted into the live stylesheet by the first
     *     button, so the browser re-checked every element of the page against it (khtmlButton.Preinstall);
     *   - the dialog's own font (Segoe UI: regular, and the semibold of its buttons) was looked up and
     *     read from disk by the first layout that needed it - a wait on another process, which is what
     *     stretched on a busy machine (_WarmFonts).
     */
    public static Preinstall = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            kModalDialogStyles.CloseStyle();
            khtmlButton.Preinstall();
            kStyles.Save();
            kModalDialogStyles.InstallSharedStyles();
            kFormTheme.Install();
            kModalDialogStyles.WarmFonts();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kModalDialogStyles.Preinstall");
        }
    }

    /**
     * Get the dialog's fonts loaded before the first dialog needs them: a few hidden words in the
     * dialog's font stack (regular, semibold, bold) are put in the page, the browser's next ordinary
     * layout resolves and loads the faces, and the words are taken out again after that frame.
     *
     * Nothing is measured and no layout is forced - the page lays the probe out when it next lays
     * itself out anyway. `visibility:hidden`, not `display:none`: an element that is not displayed
     * is never laid out, so its font is never asked for. Without a body yet (a <head> script)
     * nothing is done, and the first dialog loads the fonts as it always did.
     */
    public static WarmFonts = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (kModalDialogStyles._FontsWarmed === true) { return; }
            if (typeof document === "undefined" || document.body === null) { return; }
            kModalDialogStyles._FontsWarmed = true;
            const Probe: HTMLDivElement = document.createElement("div");
            Probe.className = "kDialogFontProbe";
            Probe.setAttribute("aria-hidden", "true");
            Probe.style.cssText = "position:absolute;left:-9999px;top:0;visibility:hidden;pointer-events:none;"
                + "white-space:nowrap;font-size:13px;";
            Probe.style.fontFamily = kModalDialogStyles.FontFamily;
            for (const Weight of ["400", "600", "700"]) {
                const Sample: HTMLSpanElement = document.createElement("span");
                Sample.style.fontWeight = Weight;
                Sample.textContent = "Ag ×";
                Probe.appendChild(Sample);
            }
            document.body.appendChild(Probe);
            kModalDialogStyles._FontProbe = Probe;
            window.requestAnimationFrame(kModalDialogStyles._OnFontProbeFrame);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kModalDialogStyles.WarmFonts");
        }
    }
    /** The frame that will lay the probe out is starting: take the probe out once it is done.
     *  (A frame callback runs BEFORE that frame's layout, so removing it here would be too early.) */
    private static _OnFontProbeFrame = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            window.setTimeout(kModalDialogStyles._RemoveFontProbe, 0);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kModalDialogStyles._OnFontProbeFrame");
        }
    }
    private static _RemoveFontProbe = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (kModalDialogStyles._FontProbe === null) { return; }
            kModalDialogStyles._FontProbe.remove();
            kModalDialogStyles._FontProbe = null;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kModalDialogStyles._RemoveFontProbe");
        }
    }

    public static InstallSharedStyles = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            kStyles.AddStyles("kDialogSharedStyles",
                ".kDialogTopRightClose:focus-visible{outline:2px solid #ffffff;outline-offset:2px;}"
                + ".kDialogContent:focus-visible{outline:2px solid #06b5ea;outline-offset:2px;}"
                // a dialog title reads as a HEADING (owner 2026-11-02: "stronger header", every dialog): bars
                // (SetGreyTitle / SetAccentTitle) keep their own padding and colours; a plain title gets a rule under it
                + ".kDialogTitle{font-size:17px;font-weight:600;line-height:1.3;color:#1f2d3d;padding-right:28px;}"
                + ".kDialogTitle:not(.kDialogTitle--accent):not(.kDialogTitle--bar){padding-bottom:10px;margin-bottom:14px;border-bottom:1px solid #e3e7eb;}"
                // Opt-in only - keyed on the attribute kMotion.Sync() sets, not on
                // the OS media query. See kMotion in kAnimateTexts.ts.
                + "html[data-kreducedmotion=\"1\"] .kDialogContainer,"
                + "html[data-kreducedmotion=\"1\"] .kDialogContainer *,"
                + "html[data-kreducedmotion=\"1\"] .kDialogContent,"
                + "html[data-kreducedmotion=\"1\"] .kDialogContent *"
                + "{transition-duration:0.01ms !important;animation-duration:0.01ms !important;"
                + "animation-iteration-count:1 !important;scroll-behavior:auto !important;}");
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogStyles.InstallSharedStyles"); }
    }
}
